/**
 * Turn anything axios threw at us into a string a human can read.
 *
 * Handles:
 *  - axios network failure (no .response)         -> "Network error — check your connection."
 *  - DRF { detail: "..." }                        -> the detail string
 *  - DRF { error: "..." } / { message: "..." }    -> the field
 *  - DRF field-validation { field: ["msg", ...] } -> "field: msg" (joined with newlines)
 *  - Plain string body                            -> the string
 *  - Anything else                                -> a fallback message + console.error so we
 *                                                    don't silently swallow shape changes
 */
export default function formatError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  if (typeof err === "string") return err;

  // Axios with no response = network / CORS / aborted
  if (err && !err.response) {
    if (err.message) return err.message;
    return "Network error — check your connection and try again.";
  }

  const data = err.response?.data;
  if (data == null) {
    return err.message || fallback;
  }
  if (typeof data === "string") {
    return data;
  }
  if (typeof data === "object") {
    if (typeof data.detail === "string") return data.detail;
    if (typeof data.error === "string") return data.error;
    if (typeof data.message === "string") return data.message;

    // DRF field-validation: { field_name: ["err msg", ...] }
    const lines = [];
    for (const [k, v] of Object.entries(data)) {
      if (Array.isArray(v)) {
        lines.push(`${k}: ${v.join(", ")}`);
      } else if (typeof v === "string") {
        lines.push(`${k}: ${v}`);
      }
    }
    if (lines.length) return lines.join("\n");

    // Unknown shape — log so we can spot-fix later, but don't show the raw object
    // to the user as "[object Object]".
    // eslint-disable-next-line no-console
    console.error("formatError: unknown error shape", data);
    return fallback;
  }
  return fallback;
}
